"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { createTranslator } from "@/lib/localization";
import { useCustomerAuthContext } from "@/app/context/CustomerAuthContext";
import { sanitizeReturnUrl } from "@/lib/return-url";
import type { LoginOutcome } from "@/lib/customer-auth-api";

type Props = {
  activeLanguage: string;
  texts?: Record<string, string> | null;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginForm({ activeLanguage, texts }: Props) {
  const t = createTranslator(texts ?? {});
  const router = useRouter();
  const searchParams = useSearchParams();
  const { login } = useCustomerAuthContext();
  const langPrefix = activeLanguage !== "en" ? `/${activeLanguage}` : "";
  const returnUrl = sanitizeReturnUrl(searchParams.get("returnUrl"), `${langPrefix}/`);
  const isReturningToAccount = returnUrl !== `${langPrefix}/` && returnUrl !== "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [status, setStatus] = useState<"idle" | "submitting">("idle");
  const [outcome, setOutcome] = useState<LoginOutcome | null>(null);

  const outcomeMessage: Record<Exclude<LoginOutcome, "Success">, string> = {
    InvalidCredentials: t("auth.login.invalidCredentials"),
    EmailNotConfirmed: t("auth.login.emailNotConfirmed"),
    AccountInactive: t("auth.login.inactiveAccount"),
    TooManyAttempts: t("auth.login.rateLimited"),
  };

  function validate(): boolean {
    const errors: { email?: string; password?: string } = {};
    if (!email.trim() || !EMAIL_PATTERN.test(email.trim())) {
      errors.email = t("validation.email");
    }
    if (!password) {
      errors.password = t("validation.required");
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setOutcome(null);
    if (!validate()) return;

    setStatus("submitting");
    const result = await login(email.trim(), password, activeLanguage, rememberMe);
    setStatus("idle");

    if (result === "Success") {
      // returnUrl is validated relative-only (sanitizeReturnUrl) — never an open redirect.
      router.push(returnUrl);
      return;
    }

    setOutcome(result);
  }

  return (
    <section style={styles.section}>
      <div style={styles.card}>
        <h1 style={styles.title}>{t("auth.login.title")}</h1>
        {isReturningToAccount ? <p style={styles.returnPrompt}>{t("auth.login.returnToAccount")}</p> : null}

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          <label style={styles.label}>
            {t("auth.login.email")}
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
            />
            {fieldErrors.email ? <span style={styles.fieldError}>{fieldErrors.email}</span> : null}
          </label>

          <label style={styles.label}>
            {t("auth.login.password")}
            <div style={styles.passwordRow}>
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={styles.toggleButton}
                aria-label={showPassword ? t("auth.register.hidePassword") : t("auth.register.showPassword")}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>
            {fieldErrors.password ? <span style={styles.fieldError}>{fieldErrors.password}</span> : null}
          </label>

          <div style={styles.rowBetween}>
            <label style={styles.checkboxLabel}>
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
              {t("auth.login.rememberMe")}
            </label>
            <Link href={`${langPrefix}/forgot-password`} style={styles.link}>
              {t("auth.forgotPassword")}
            </Link>
          </div>

          {outcome && outcome !== "Success" ? <p style={styles.errorMessage}>{outcomeMessage[outcome]}</p> : null}

          <button type="submit" disabled={status === "submitting"} style={styles.submitButton}>
            {status === "submitting" ? t("auth.login.submitting") : t("auth.login.submit")}
          </button>
        </form>

        <div style={styles.footer}>
          <span>{t("auth.registerPrompt")}</span>{" "}
          <Link href={`${langPrefix}/register`} style={styles.link}>
            {t("auth.registerLink")}
          </Link>
        </div>
        {outcome === "EmailNotConfirmed" ? (
          <div style={styles.footer}>
            <Link href={`${langPrefix}/verify-email`} style={styles.link}>
              {t("auth.login.resendConfirmationLink")}
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { marginTop: 16, padding: "0 16px 48px" },
  card: {
    maxWidth: 460,
    margin: "0 auto",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1.5rem",
    background: "#fff",
  },
  title: { margin: "0 0 0.4rem", fontSize: "1.5rem", color: "#0f172a", fontWeight: 700, textAlign: "center" as const },
  returnPrompt: { margin: "0 0 1.2rem", fontSize: "0.85rem", color: "#64748b", textAlign: "center" as const },
  form: { display: "grid", gap: 14 },
  label: { display: "grid", gap: 4, fontSize: "0.85rem", color: "#334155", fontWeight: 600 },
  input: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0.6rem 0.75rem",
    fontSize: "0.95rem",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  passwordRow: { display: "flex", gap: 6, alignItems: "center" },
  toggleButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#f8fafc",
    padding: "0.5rem 0.7rem",
    cursor: "pointer",
    fontSize: "0.95rem",
  },
  rowBetween: { display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.85rem" },
  checkboxLabel: { display: "flex", alignItems: "center", gap: 6, color: "#334155", fontWeight: 500 },
  fieldError: { fontSize: "0.78rem", color: "#dc2626" },
  errorMessage: { margin: 0, color: "#dc2626", fontSize: "0.88rem", fontWeight: 600, textAlign: "center" as const },
  submitButton: {
    marginTop: 6,
    border: "none",
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    padding: "0.75rem 1rem",
    fontSize: "1rem",
    cursor: "pointer",
  },
  footer: { marginTop: "1rem", textAlign: "center" as const, fontSize: "0.88rem", color: "#64748b" },
  link: { color: "#2563eb", fontWeight: 600, textDecoration: "none" },
};
